"use client"

import {AppSeparator} from "@/app/components/common/app-separator/AppSeparator";
import React, {HTMLAttributes, useEffect, useState, useSyncExternalStore} from "react";
import {cn} from "@/lib/utils";
import {EighteenPlusSvg} from "@/app/components/icons/EighteenPlusSvg";
import {Link} from "@/i18n/navigation";
import {useTranslations} from "next-intl";
import {BadgeQuestionMark} from "lucide-react";
import {MainCtaBtn} from "@/app/components/common/main-cta-btn/MainCtaBtn";
import AnjImage from "@/app/components/common/anj-image/AnjImage";
import {useZendeskWidget} from "@/hooks/useZendeskWidget";
import {DOCS_NAV} from "@/lib/utils/contants/docsNav";
import Image from "next/image";
import {Icon} from "@/app/components/common/Icon";

const RESTRICTED_DOMAINS = [
    'alddev.xyz',
    'www.alddev.xyz',
];

// The copyright year, hydration-safe. React renders the UTC year on the server
// and during hydration, then swaps in the visitor's local year. Reading the
// local year during render mismatches for UTC-behind timezones in the hours
// after New Year, because Vercel runs UTC (AC-655).
const subscribeToYear = () => () => {
};
const getLocalYear = () => new Date().getFullYear();
const getUtcYear = () => new Date().getUTCFullYear();

export const AppFooter = ({className}: HTMLAttributes<HTMLDivElement>) => {
    const [showLicense, setShowLicense] = useState(false);
    const year = useSyncExternalStore(subscribeToYear, getLocalYear, getUtcYear);
    const {onOpenWidget} = useZendeskWidget();

    // const POLICIES = [
    //     {
    //         label: 'Polices.termsOfService',
    //         href: '/docs/terms'
    //     },
    //     {
    //         label: 'Polices.kycPolicy',
    //         href: '/docs/kyc-policy'
    //     },
    //     {
    //         label: 'Polices.responsibleGambling',
    //         href: '/docs/responsible-gambling'
    //     },
    // ]

    const COMMUNITIES = [
        {
            label: 'Communities.x',
            link: 'https://x.com/Aldex_Win'
        },
        {
            label: 'Communities.instagram',
            link: 'https://www.instagram.com/aldex.win?igsh=OGx2bzJuMXVqcWFn',
        },
        // {
        //     label: 'Communities.tiktok'
        // },
        // {
        //     label: 'Communities.facebook'
        // },
        // {
        //     label: 'Communities.telegram'
        // },
    ]

    const t = useTranslations();

    useEffect(() => {
        const allowed = !RESTRICTED_DOMAINS.includes(window.location.hostname);
        setTimeout(() => {
            setShowLicense(allowed);
        }, 100)
    }, []);


    return (
        <footer className={cn('bg-dark-gray pt-[64px] md:pb-[84px]', className)}>
            <div className={'flex flex-col lg:flex-row lg:justify-between gap-[48px] lg:gap-[100px] mb-[48px]'}>
                <Link prefetch={false} href={'/'}>
                    <Image width={103} height={12} className={'w-[102px] h-[12px] md:w-[175px] md:h-[21px] active:scale-90 transition-transform duration-300'}
                           src={'/icons/logo.svg'} alt={'Logo'}/>
                </Link>

                <p className={'text-noble-black-300 leading-[143%] text-[14px] lg:mb-0 lg:order-2 lg:basis-[628px] lg:shrink-1'}>
                    {t.rich('AppFooter.description', {
                        site: (chunks) => <Link prefetch={false} href={'/'}>{chunks}</Link>
                    })}
                    <br/>
                    <br/>
                    {t('AppFooter.contactUsAt')} <a href={'mailto:support@aldex.com'}>support@aldex.com</a>.
                </p>

                <div className={'flex gap-[24px] lg:order-1 flex-wrap lg:flex-nowrap'}>
                    <div className={'flex-1'}>
                        <div
                            className={'text-noble-black-300 mb-[16px] uppercase text-[10px] leading-[150%] tracking-[35%]'}>{t('AppFooter.policy')}</div>
                        <ul className={'text-noble-black-0 text-[12px] font-medium wrap-break-word'}>
                            {DOCS_NAV.map((i) => (
                                <li key={i.title} className={'mb-[8px] leading-[18px] text-nowrap'}>
                                    <Link prefetch={false} href={i.href}>{t(i.title)}</Link>
                                </li>
                            ))}
                        </ul>
                    </div>
                    <div className={'flex-1'}>
                        <div
                            className={'text-noble-black-300 mb-[16px] uppercase text-[10px] leading-[150%] tracking-[35%]'}>{t('AppFooter.community')}</div>
                        <ul className={'text-noble-black-0 text-[12px] font-medium'}>
                            {COMMUNITIES.map((i) => (
                                <li key={i.label} className={'mb-[8px] leading-[18px]'}>
                                    <a href={i.link} target={'_blank'}>{t(i.label)}</a>
                                </li>
                            ))}
                        </ul>
                    </div>
                </div>
            </div>

            <AppSeparator className={'mb-[36px]'}/>

            <div className={'flex flex-col lg:flex-row lg:justify-between lg:items-center gap-[16px]'}>
                <p className={'text-noble-black-300 uppercase text-[10px] leading-[150%] tracking-[35%]'}>
                    © {year} Aldex.com | {t('AppFooter.allRightsReserved')}
                </p>

                <div className={'text-noble-black-300 flex items-center gap-[6px]'}>
                    {showLicense ? <AnjImage/> : null}
                    <Icon width={32} height={32} name={'eighteenPlus'} />
                </div>
            </div>

            <div className={'md:hidden flex justify-end'}>
                <MainCtaBtn onClick={onOpenWidget}>
                    <BadgeQuestionMark />
                </MainCtaBtn>
            </div>
        </footer>
    )
}
